import { PlusIcon, Trash01Icon } from '@langchain/untitled-ui-icons';

import { useCallback } from 'react';

import { Button, IconButton, Input } from '@/design-system';

import { InsightsJobFormData, PartitionPair } from '../types';
import { ErrorText, FormSection } from './ConfigSectionComponents';

export const CategoriesSection = ({
  formData,
  updateFormData,
  errors,
}: {
  formData: InsightsJobFormData;
  updateFormData: (updates: Partial<InsightsJobFormData>) => void;
  errors: Partial<Record<keyof InsightsJobFormData, string>>;
}) => {
  // Helper functions for managing partition pairs
  const addPartitionPair = useCallback(() => {
    const newPair: PartitionPair = {
      key: '',
      value: '',
      id: `partition-${Date.now()}-${Math.random()}`,
    };
    updateFormData({
      partitionPairs: [...formData.partitionPairs, newPair],
    });
  }, [formData.partitionPairs, updateFormData]);

  const removePartitionPair = useCallback(
    (id: string) => {
      updateFormData({
        partitionPairs: formData.partitionPairs.filter(
          (pair) => pair.id !== id
        ),
      });
    },
    [formData.partitionPairs, updateFormData]
  );

  const updatePartitionPair = useCallback(
    (id: string, key: string, value: string) => {
      updateFormData({
        partitionPairs: formData.partitionPairs.map((pair) =>
          pair.id === id ? { ...pair, key, value } : pair
        ),
      });
    },
    [formData.partitionPairs, updateFormData]
  );

  return (
    <FormSection
      title="Create categories (optional)"
      titleVariant="h2"
      description="Choose how your data is grouped and how the results are summarized. You can optionally set categories for your data to be clustered into, or let us generate them automatically."
    >
      {formData.partitionPairs.map((pair) => (
        <div key={pair.id} className="flex items-start gap-2">
          <div className="flex-1">
            <Input
              placeholder="e.g. Product Area"
              value={pair.key}
              onChange={(value) =>
                updatePartitionPair(pair.id, value, pair.value)
              }
              className="text-sm"
            />
          </div>
          <div className="flex-[2]">
            <Input
              placeholder="e.g. Description of Product Area"
              value={pair.value}
              onChange={(value) =>
                updatePartitionPair(pair.id, pair.key, value)
              }
              className="text-sm"
            />
          </div>
          <div className="my-auto">
            <IconButton
              label="Remove category"
              variant="plain"
              color="error"
              onClick={() => removePartitionPair(pair.id)}
              icon={Trash01Icon}
            />
          </div>
        </div>
      ))}
      <Button
        variant="underlined"
        size="sm"
        color="secondary"
        onClick={addPartitionPair}
        leftDecorator={PlusIcon}
        className="mr-auto"
      >
        Add Category
      </Button>
      <ErrorText error={errors.partitionPairs} />
    </FormSection>
  );
};
